import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 2 * 60 * 1000;
// A new buildId must be seen on this many consecutive polls before we prompt,
// so a single flaky/stale response from an edge cache can't trigger the dialog.
const CONSECUTIVE_POLLS_REQUIRED = 2;
const SEEN_BUILD_IDS_KEY = 'sw-version-check-seen-build-ids';
const MAX_SEEN_BUILD_IDS = 20;
const PENDING_BUILD_ID_KEY = 'sw-version-check-pending-build-id';
const CACHE_BUST_PARAM = '_vc';

async function fetchCurrentBuildId(): Promise<string | null> {
  try {
    const res = await fetch(window.location.href, { cache: 'no-store' });
    if (!res.ok) {
      console.log('[version-check] page fetch failed:', res.status);
      return null;
    }
    const text = await res.text();
    const match = text.match(/"buildId":"([^"]+)"/);
    const buildId = match ? match[1] : null;
    console.log('[version-check] polled buildId:', buildId);
    return buildId;
  } catch (err) {
    console.log('[version-check] fetch error new:', err);
    return null;
  }
}

function readSeenBuildIds(): string[] {
  try {
    const raw = window.localStorage.getItem(SEEN_BUILD_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function rememberBuildId(buildId: string) {
  try {
    const seen = readSeenBuildIds().filter((id) => id !== buildId);
    seen.push(buildId);
    window.localStorage.setItem(SEEN_BUILD_IDS_KEY, JSON.stringify(seen.slice(-MAX_SEEN_BUILD_IDS)));
  } catch {
    // localStorage unavailable — the consecutive-poll debounce still applies.
  }
}

function stripCacheBustParam() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(CACHE_BUST_PARAM)) {
      url.searchParams.delete(CACHE_BUST_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // Leaving the param in the URL is harmless.
  }
}

export function useVersionCheck() {
  const initialId = useRef<string | null>(null);
  const candidate = useRef<{ id: string; hits: number } | null>(null);
  const [isOutdated, setIsOutdated] = useState(false);

  useEffect(() => {
    initialId.current = (window as any).__NEXT_DATA__?.buildId ?? null;
    console.log('[version-check] initial buildId:', initialId.current);
    if (initialId.current) {
      rememberBuildId(initialId.current);
    }
    stripCacheBustParam();

    try {
      const pending = window.sessionStorage.getItem(PENDING_BUILD_ID_KEY);
      if (pending) {
        window.sessionStorage.removeItem(PENDING_BUILD_ID_KEY);
        if (pending !== initialId.current) {
          // The cache-busted reload didn't land on the buildId that triggered the
          // prompt, so that id was a stale copy — remember it so it can't re-trigger.
          console.log('[version-check] reload landed on different buildId, marking as stale:', pending);
          rememberBuildId(pending);
        }
      }
    } catch {
      // sessionStorage unavailable — skip the post-reload reconciliation.
    }

    const interval = setInterval(async () => {
      const current = await fetchCurrentBuildId();
      if (!current || !initialId.current || current === initialId.current) {
        candidate.current = null;
        return;
      }
      if (readSeenBuildIds().includes(current)) {
        // This browser has already run that build, so a CDN/proxy served us a
        // stale cached page — it is not a new release.
        console.log('[version-check] ignoring previously-seen buildId (stale cache):', current);
        candidate.current = null;
        return;
      }
      if (candidate.current?.id === current) {
        candidate.current.hits += 1;
      } else {
        candidate.current = { id: current, hits: 1 };
      }
      console.log('[version-check] comparing:', {
        initial: initialId.current,
        current,
        consecutiveHits: candidate.current.hits,
      });
      if (candidate.current.hits >= CONSECUTIVE_POLLS_REQUIRED) {
        console.log('[version-check] new version confirmed — showing banner');
        setIsOutdated(true);
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const reloadToLatest = useCallback(() => {
    try {
      if (candidate.current?.id) {
        window.sessionStorage.setItem(PENDING_BUILD_ID_KEY, candidate.current.id);
      }
    } catch {
      // sessionStorage unavailable — reload without reconciliation.
    }
    try {
      const url = new URL(window.location.href);
      // Change the URL so browser and CDN caches can't serve the old HTML back.
      url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }, []);

  return { isOutdated, reloadToLatest };
}
